import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { autoUpdate, computePosition, shift } from '@floating-ui/dom';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  BLUR_COMMAND,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_NORMAL,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { CLOSE_TOOLBAR } from '@mutiny-pkg/bin-ui/src/richtext/RichInput/lexicalConfig';
import { LexicalThemeColors } from '../types';

const DEFAULT_DOM_ELEMENT = typeof document !== 'undefined' ? document.body : undefined;

type FloatingMenuCoords = { x: number; y: number } | undefined;

export type FloatingMenuComponentProps = {
  editor: ReturnType<typeof useLexicalComposerContext>[0];
  shouldShow: boolean;
  options?: {
    enableTextAlign?: boolean;
    enableExtendedTextFormatting?: boolean;
    themeColors?: LexicalThemeColors;
    onColorChange?: (color: string, isFullSelection: boolean, type: 'text' | 'highlight') => boolean | void;
  };
};

export type FloatingMenuPluginProps = {
  element?: HTMLElement;
  MenuComponent?: React.FC<FloatingMenuComponentProps>;
  options?: {
    enableTextAlign?: boolean;
    enableExtendedTextFormatting?: boolean;
    themeColors?: LexicalThemeColors;
    onColorChange?: (color: string, isFullSelection: boolean, type: 'text' | 'highlight') => boolean | void;
  };
};

export function FloatingMenuPlugin({ element, MenuComponent, options }: FloatingMenuPluginProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<FloatingMenuCoords>(undefined);
  const [editor] = useLexicalComposerContext();
  const [referenceEl, setReferenceEl] = useState<Range | null>(null);
  const show = coords !== undefined;

  const calculatePosition = useCallback(() => {
    if (!referenceEl || !ref.current) {
      setCoords(undefined);
      return;
    }

    computePosition(referenceEl, ref.current, { placement: 'top', middleware: [shift()] })
      .then((pos) => {
        setCoords({ x: pos.x, y: pos.y - 10 });
      })
      .catch(() => {
        setCoords(undefined);
      });
  }, [referenceEl]);

  const clearFloatingMenu = useCallback(() => {
    setCoords(undefined);
    setReferenceEl(null);
  }, [setCoords, setReferenceEl]);

  const $handleSelectionChange = useCallback(() => {
    if (editor.isComposing()) return false;

    const isEditorFocused =
      editor.getRootElement() !== (editor?.getRootElement()?.getRootNode() as Document | ShadowRoot)?.activeElement;
    if (isEditorFocused) {
      clearFloatingMenu();
      return true;
    }

    const selection = $getSelection();

    if ($isRangeSelection(selection) && !selection.anchor.is(selection.focus)) {
      const domSelection = getSelection();
      const domRange = domSelection && domSelection.rangeCount !== 0 ? domSelection.getRangeAt(0) : null;
      setReferenceEl(domRange);
    } else {
      clearFloatingMenu();
    }

    return true;
  }, [editor, clearFloatingMenu]);

  useEffect(() => {
    return editor.registerCommand(SELECTION_CHANGE_COMMAND, $handleSelectionChange, COMMAND_PRIORITY_NORMAL);
  }, [editor, $handleSelectionChange]);

  useEffect(() => {
    return editor.registerCommand(
      BLUR_COMMAND,
      (event) => {
        // do not reset coords if user clicks on the popup
        if (ref.current && !ref.current.contains(event.relatedTarget as Node)) {
          clearFloatingMenu();
          return false;
        }
        return true;
      },
      COMMAND_PRIORITY_EDITOR,
    );
  }, [editor, clearFloatingMenu]);

  useEffect(() => {
    return editor.registerCommand(
      CLOSE_TOOLBAR,
      () => {
        setCoords(undefined);
        setReferenceEl(null);
        return false;
      },
      COMMAND_PRIORITY_EDITOR,
    );
  }, [editor]);

  useEffect(() => {
    if (!referenceEl || !ref.current) return;

    // This allows floating ui to auto update the placement of menu.
    // Reference: https://floating-ui.com/docs/computeposition#anchoring
    const cleanup = autoUpdate(referenceEl, ref.current, calculatePosition, { animationFrame: true });
    // eslint-disable-next-line consistent-return
    return () => cleanup();
  }, [calculatePosition, referenceEl]);

  if (!MenuComponent) return null;

  if (!element && !DEFAULT_DOM_ELEMENT) {
    return null;
  }

  return createPortal(
    <div
      ref={ref}
      aria-hidden={!show}
      style={{
        position: 'absolute',
        top: coords?.y,
        left: coords?.x,
        visibility: show ? 'visible' : 'hidden',
        display: show ? 'block' : 'none',
        zIndex: 1500,
        opacity: show ? 1 : 0,
      }}
    >
      <MenuComponent editor={editor} shouldShow={show} options={options} />
    </div>,
    element ?? DEFAULT_DOM_ELEMENT!,
  );
}
